'use client';

import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { keepPreviousData } from '@tanstack/react-query';

import { operations } from '@/api/v1/query';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';

type Customer = ApiSchemas['CustomerRead'];
type CustomerPartialWithId = Partial<Customer> & { id: number };

export type CustomersState = Record<string, Customer>;

// Actions for our reducer (kept intentionally small).
export type Action =
  | { type: 'UPSERT_ONE'; customer: CustomerPartialWithId }
  | { type: 'UPSERT_MANY'; customers: Customer[] };

function reducer(state: CustomersState, action: Action): CustomersState {
  switch (action.type) {
    case 'UPSERT_ONE':
      const existing = state[action.customer.id] || {};
      return { ...state, [action.customer.id]: { ...existing, ...action.customer } };
    case 'UPSERT_MANY':
      return {
        ...state,
        ...action.customers.reduce<CustomersState>((acc, c) => {
          acc[c.id] = c;
          return acc;
        }, {}),
      };
    default:
      return state;
  }
}

interface CustomerContextValue {
  customers: CustomersState;
  upsertOne: (customer: CustomerPartialWithId) => void;
  upsertMany: (customers: Customer[]) => void;
}

const CustomerContext = createContext<CustomerContextValue | null>(null);

/**
 * Provider component — wrap your <QueryClientProvider> tree with this.
 */
export function CustomerProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, {});

  const upsertOne = useCallback((customer: CustomerPartialWithId) => {
    dispatch({ type: 'UPSERT_ONE', customer });
  }, []);
  const upsertMany = useCallback((customers: Customer[]) => {
    dispatch({ type: 'UPSERT_MANY', customers });
  }, []);

  return (
    <CustomerContext.Provider value={{ customers: state, upsertOne, upsertMany }}>{children}</CustomerContext.Provider>
  );
}

export function useCustomerCtx() {
  const ctx = useContext(CustomerContext);
  if (!ctx) {
    throw new Error('useCustomerCtx must be used within a CustomerProvider');
  }
  return ctx;
}

const CustomerSkeleton: Customer = {
  id: '',
  office_id: '',
  status: 'ACTIVE',
  created_at: '',
  updated_at: '',
  name: '',
  first_name: '',
  last_name: '',
  contacts: [],
  commercial: false,
  locations: [],
  preferences: {
    technicians: [],
    days: [],
    dates: [],
    enforce_days: false,
  },
};

export function useCustomer(id: string) {
  const { customers } = useCustomerCtx();
  return customers[id] || CustomerSkeleton;
}

export function useCustomers(ids: string[]) {
  const { customers } = useCustomerCtx();
  return useMemo(() => ids.map((id) => customers[id]).filter(Boolean), [customers, ids]);
}

type ApiCustomersListRequest = operations['search_customers']['parameters']['query'];

export function useApiCustomers(query: ApiCustomersListRequest) {
  const { upsertMany } = useCustomerCtx();
  const { api } = useApiV1Ctx();
  const result = api.customers.searchCustomers.useQuery(
    {
      query: {
        page: query.page,
        limit: query.limit,
        name_query: query.name_query,
        phone_query: query.phone_query,
        address_query: query.address_query,
        external_id: query.external_id,
        status: query.status,
      },
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      placeholderData: keepPreviousData,
    }
  );

  useEffect(() => {
    if (result?.data?.items) {
      upsertMany(result.data.items);
    }
  }, [result?.data?.items, upsertMany]);

  return result;
}
